/* section-sticky-images-and-text.js
   - Coverflow UNIQUEMENT en desktop si data-coverflow-desktop="true"
   - Desktop: slide au trackpad (2 doigts) SANS cliquer
   - Fix: empêche le “scroll infini” horizontal de la page
   - Mobile: scroll natif + progress
   - Vertical: sticky existant
*/

class StickyImagesAndText {
  constructor(section) {
    this.section = section;
    this.sectionId = section.id;

    this.isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.layout = section.dataset.layout || 'vertical';

    this.isCoverflowDesktop = section.getAttribute('data-coverflow-desktop') === 'true';

    if (this.isReducedMotion) return;

    this.body = section.querySelector('.sticky-images-and-text__body');
    this.content = section.querySelector('.sticky-images-and-text__content');
    this.aside = section.querySelector('.sticky-images-and-text__aside');
    this.imagesContainer = section.querySelector('.sticky-images-and-text__images');

    this.textBlocks = section.querySelectorAll('.sticky-text');
    this.imageBlocks = section.querySelectorAll('.sticky-images-and-text__image');
    this.dots = section.querySelectorAll('.sticky-images-and-text__dot');

    this.horizontalSliderEl = section.querySelector('[data-horizontal-slider] .swiper');
    this.horizontalWrapperEl = section.querySelector('[data-horizontal-slider]');

    this.activeIndex = 0;
    this.isScrolling = false;
    this.isMobile = window.innerWidth <= 989;

    this.swiper = null;

    this.handleResize = null;
    this.handleImageScroll = null;
    this.handleHorizontalResize = null;
    this.horizontalMobileScrollHandler = null;

    // Wheel blocker page-level (desktop)
    this.preventDesktopInfiniteScrollHandler = null;

    // IntersectionObserver pour dots desktop vertical (plus fiable que scroll)
    this.verticalIntersectionObserver = null;
    this.scrollRootFallback = null; // pour retirer le listener dans destroy()

    this.init();
  }

  init() {
    // HORIZONTAL
    if (this.layout === 'horizontal' && this.horizontalSliderEl) {
      if (!this.isMobile) {
        this.initHorizontalSlider();         // desktop (Swiper)
        this.preventDesktopInfiniteScroll(); // bloque scroll page à droite
      } else {
        this.setupHorizontalMobileProgress(); // mobile (scroll natif)
      }
      this.setupHorizontalResizeListener();
      return;
    }

    // VERTICAL
    if (!this.body || !this.content || !this.aside) return;

    this.activeIndex = 0;
    this.updateActiveStates();

    this.waitForImages().then(() => {
      this.setupEventListeners();
      if (this.isMobile) {
        this.updateMobileScrollSync();
      } else {
        this.updateActiveBlock();
      }
    });
  }

  /* =========================================================
     HORIZONTAL DESKTOP (SWIPER)
     ========================================================= */
  initHorizontalSlider() {
    if (typeof Swiper === 'undefined' || !this.horizontalSliderEl) return;
    if (this.swiper) return;

    const fillEl = this.section.querySelector('[data-progress-fill]');
    const motoEl = this.section.querySelector('.sticky-images-and-text__moto');

    const updateProgress = (progress) => {
      const p = Math.max(0, Math.min(1, progress || 0));
      if (fillEl) fillEl.style.width = (p * 100) + '%';
      if (motoEl) motoEl.style.left = (p * 100) + '%';
    };

    const baseOn = {
      init: (swiper) => updateProgress(swiper.progress ?? 0),
      progress: (swiper) => updateProgress(swiper.progress ?? 0),
      slideChange: (swiper) => updateProgress(swiper.progress ?? 0),
      resize: (swiper) => updateProgress(swiper.progress ?? 0)
    };

    const mousewheelCfg = {
      forceToAxis: true,
      sensitivity: 1,
      releaseOnEdges: false,
      thresholdDelta: 2,
      thresholdTime: 0
    };

    const swiperOptions = {
      slidesPerView: 'auto',
      centeredSlides: true,
      initialSlide: 1,
      spaceBetween: 24,
      slidesPerGroup: 1,
      loop: false,
      freeMode: false,
      grabCursor: true,
      watchSlidesProgress: true,
      slideToClickedSlide: true,
      speed: 600,
      mousewheel: mousewheelCfg,
      on: baseOn
    };

    this.swiper = new Swiper(this.horizontalSliderEl, swiperOptions);

    // Stabilise après load (lang switch / fonts / images)
    const hardUpdate = () => {
      if (!this.swiper) return;
      this.swiper.update();
      updateProgress(this.swiper.progress ?? 0);
    };

    requestAnimationFrame(hardUpdate);
    window.addEventListener('load', hardUpdate, { once: true });
  }

  /* =========================================================
     FIX: empêche la page de partir à droite (trackpad)
     On bloque le wheel horizontal UNIQUEMENT quand l’event vient de la section
     ========================================================= */
  preventDesktopInfiniteScroll() {
    if (this.isMobile) return;

    this.preventDesktopInfiniteScrollHandler = (e) => {
      // on ne touche qu’aux gestures qui se passent dans la section
      if (!this.section.contains(e.target)) return;

      // si c'est un scroll horizontal (trackpad)
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        // laisse Swiper le gérer, bloque la page
        e.preventDefault();
      }
    };

    window.addEventListener('wheel', this.preventDesktopInfiniteScrollHandler, {
      passive: false,
      capture: true
    });
  }

  /* =========================================================
     HORIZONTAL MOBILE (SCROLL NATIF + PROGRESS)
     ========================================================= */
  setupHorizontalMobileProgress() {
    const wrapperEl = this.section.querySelector('[data-horizontal-slider]');
    const fillEl = this.section.querySelector('[data-progress-fill]');
    const motoEl = this.section.querySelector('.sticky-images-and-text__moto');
    if (!wrapperEl || !fillEl) return;

    const updateFromScroll = () => {
      const scrollWidth = wrapperEl.scrollWidth;
      const clientWidth = wrapperEl.clientWidth;
      const maxScroll = scrollWidth - clientWidth;
      const progress = maxScroll > 0 ? wrapperEl.scrollLeft / maxScroll : 0;

      fillEl.style.width = (progress * 100) + '%';
      if (motoEl) motoEl.style.left = (progress * 100) + '%';
    };

    updateFromScroll();
    wrapperEl.addEventListener('scroll', updateFromScroll, { passive: true });
    this.horizontalMobileScrollHandler = updateFromScroll;
  }

  setupHorizontalResizeListener() {
    this.handleHorizontalResize = debounce(() => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 989;

      const wrapperEl = this.section.querySelector('[data-horizontal-slider]');

      // Desktop -> Mobile
      if (!wasMobile && this.isMobile) {
        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }

        if (this.preventDesktopInfiniteScrollHandler) {
          window.removeEventListener('wheel', this.preventDesktopInfiniteScrollHandler, {
            passive: false,
            capture: true
          });
          this.preventDesktopInfiniteScrollHandler = null;
        }

        this.setupHorizontalMobileProgress();
      }

      // Mobile -> Desktop
      if (wasMobile && !this.isMobile) {
        if (wrapperEl && this.horizontalMobileScrollHandler) {
          wrapperEl.removeEventListener('scroll', this.horizontalMobileScrollHandler);
          this.horizontalMobileScrollHandler = null;
        }
        this.initHorizontalSlider();
        this.preventDesktopInfiniteScroll();
      }
    }, 250);

    window.addEventListener('resize', this.handleHorizontalResize);
  }

  /* =========================================================
     VERTICAL (sticky + dots)
     ========================================================= */
  waitForImages() {
    return new Promise((resolve) => {
      const images = this.section.querySelectorAll('img, video');
      let loadedCount = 0;
      const totalImages = images.length;

      if (totalImages === 0) {
        resolve();
        return;
      }

      const checkComplete = () => {
        loadedCount++;
        if (loadedCount >= totalImages) setTimeout(resolve, 100);
      };

      images.forEach((img) => {
        if (img.tagName === 'VIDEO') {
          if (img.readyState >= 3) checkComplete();
          else {
            img.addEventListener('loadeddata', checkComplete, { once: true });
            img.addEventListener('error', checkComplete, { once: true });
          }
          return;
        }

        if (img.complete) checkComplete();
        else {
          img.addEventListener('load', checkComplete, { once: true });
          img.addEventListener('error', checkComplete, { once: true });
        }
      });
    });
  }

  setupDesktopIntersectionObserver() {
    if (!this.imageBlocks.length || typeof IntersectionObserver === 'undefined') return;
    if (this.verticalIntersectionObserver) this.verticalIntersectionObserver.disconnect();

    /* L'IntersectionObserver est plus fiable que window scroll (conteneurs custom, trackpad, etc.) */
    this.verticalIntersectionObserver = new IntersectionObserver(
      () => {
        if (this.isScrolling) return;
        this.updateActiveBlock();
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]
      }
    );

    this.imageBlocks.forEach((img) => this.verticalIntersectionObserver.observe(img));
  }

  setupEventListeners() {
    if (!this.isMobile) {
      this.setupDesktopIntersectionObserver();
      // Double détection : IntersectionObserver + scroll window (max compatibilité)
      const boundHandleScroll = () => {
        if (this.isScrolling) return;
        requestAnimationFrame(() => this.updateActiveBlock());
      };
      this.handleScroll = boundHandleScroll;
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      // Fallback si le scroll est sur document (certains thèmes)
      const scrollRoot = document.scrollingElement || document.documentElement;
      if (scrollRoot && scrollRoot !== document.body) {
        scrollRoot.addEventListener('scroll', this.handleScroll, { passive: true });
        this.scrollRootFallback = scrollRoot;
      }
    }

    if (this.isMobile && this.imagesContainer) {
      this.handleImageScroll = debounce(() => this.updateActiveIndexFromScroll(), 0);
      this.imagesContainer.addEventListener('scroll', this.handleImageScroll, { passive: true });
    }

    this.handleResize = debounce(() => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 989;

      if (wasMobile !== this.isMobile) {
        this.destroy();
        this.setupEventListeners();
      }

      if (!this.isMobile) this.updateActiveBlock();
      else this.updateMobileScrollSync();
    }, 250);

    window.addEventListener('resize', this.handleResize);

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.scrollToImage(index));
    });

    document.addEventListener('shopify:section:load', (event) => {
      if (event.detail.sectionId && event.target.querySelector(`#${this.sectionId}`)) {
        setTimeout(() => {
          if (!this.isMobile) this.updateActiveBlock();
          else this.updateMobileScrollSync();
        }, 100);
      }
    });
  }

  handleScroll() {
    if (this.isScrolling) return;
    requestAnimationFrame(() => this.updateActiveBlock());
  }

  updateMobileScrollSync() {
    if (!this.imagesContainer) return;
    this.updateActiveIndexFromScroll();
  }

  updateActiveIndexFromScroll() {
    if (!this.imagesContainer) return;

    const scrollLeft = this.imagesContainer.scrollLeft;
    const imageWidth = window.innerWidth;
    const newActiveIndex = Math.round(scrollLeft / imageWidth);

    if (newActiveIndex !== this.activeIndex && newActiveIndex >= 0 && newActiveIndex < this.imageBlocks.length) {
      this.activeIndex = newActiveIndex;
      this.updateActiveStates();
    }
  }

  updateActiveBlock() {
    let newActiveIndex = 0;
    let maxVisibility = 0;

    this.imageBlocks.forEach((image, index) => {
      const rect = image.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const visibleTop = Math.max(0, rect.top);
      const visibleBottom = Math.min(viewportHeight, rect.bottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const visibility = visibleHeight / viewportHeight;

      if (visibility > maxVisibility) {
        maxVisibility = visibility;
        newActiveIndex = index;
      }
    });

    if (newActiveIndex !== this.activeIndex) {
      this.activeIndex = newActiveIndex;
      this.updateActiveStates();
    }
  }

  updateActiveStates() {
    this.textBlocks.forEach((block, index) => {
      block.classList.toggle('is-active', index === this.activeIndex);
    });

    this.dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === this.activeIndex);
    });
  }

  scrollToImage(index) {
    if (index < 0 || index >= this.imageBlocks.length) return;

    this.isScrolling = true;

    if (this.isMobile && this.imagesContainer) {
      this.imagesContainer.scrollTo({ left: index * window.innerWidth, behavior: 'smooth' });
    } else {
      const targetImage = this.imageBlocks[index];
      const rect = targetImage.getBoundingClientRect();
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const targetPosition = scrollTop + rect.top - (window.innerHeight / 2) + (rect.height / 2);
      window.scrollTo({ top: targetPosition, behavior: 'smooth' });
    }

    setTimeout(() => (this.isScrolling = false), 1000);
  }

  destroy() {
    if (this.handleScroll) {
      window.removeEventListener('scroll', this.handleScroll);
      if (this.scrollRootFallback) {
        this.scrollRootFallback.removeEventListener('scroll', this.handleScroll);
        this.scrollRootFallback = null;
      }
    }
    if (this.handleResize) window.removeEventListener('resize', this.handleResize);

    if (this.verticalIntersectionObserver) {
      this.verticalIntersectionObserver.disconnect();
      this.verticalIntersectionObserver = null;
    }

    if (this.imagesContainer && this.handleImageScroll) {
      this.imagesContainer.removeEventListener('scroll', this.handleImageScroll);
    }

    if (this.handleHorizontalResize) window.removeEventListener('resize', this.handleHorizontalResize);

    const wrapperEl = this.section.querySelector('[data-horizontal-slider]');
    if (wrapperEl && this.horizontalMobileScrollHandler) {
      wrapperEl.removeEventListener('scroll', this.horizontalMobileScrollHandler);
      this.horizontalMobileScrollHandler = null;
    }

    if (this.preventDesktopInfiniteScrollHandler) {
      window.removeEventListener('wheel', this.preventDesktopInfiniteScrollHandler, {
        passive: false,
        capture: true
      });
      this.preventDesktopInfiniteScrollHandler = null;
    }

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

(function initStickyImagesAndText() {
  const instances = new WeakMap();

  const boot = (root = document) => {
    root.querySelectorAll('[data-section-type="sticky-images-and-text"]').forEach((section) => {
      if (instances.has(section)) return;
      instances.set(section, new StickyImagesAndText(section));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target) boot(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (!event.target) return;
    event.target.querySelectorAll('[data-section-type="sticky-images-and-text"]').forEach((section) => {
      const inst = instances.get(section);
      if (inst && typeof inst.destroy === 'function') inst.destroy();
      instances.delete(section);
    });
  });
})();